import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, FormControl, InputLabel, Select, MenuItem,
  Box, Typography, Divider, Chip
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getIcon } from '../../../utils/IconHandler';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { ORDER_STATUS, getOrderStatusOptions, getOrderStatusColor, getOrderStatusText } from '../../../utils/Enums';
import SnackbarUtils from '../../../utils/SnackbarUtils';
import { formatMoney } from '../../../utils/currency';

const EditOrderStatusModal = ({ open, onClose, onSave, order }) => {
  const { t } = useTranslation();

  const [orderData, setOrderData] = useState({
    status: ORDER_STATUS.PENDING,
    deliveryDate: null
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (order && open) {
      setOrderData({
        status: order.status || ORDER_STATUS.PENDING,
        deliveryDate: order.deliveryDate ? dayjs(order.deliveryDate) : null
      });
    }
  }, [order, open]);

  const handleFieldChange = (field, value) => {
    setOrderData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Si se cambia a "Entregado" y no hay fecha de entrega, establecer fecha actual
    if (field === 'status' && value === ORDER_STATUS.FINALIZED && !orderData.deliveryDate) {
      setOrderData(prev => ({
        ...prev,
        deliveryDate: dayjs()
      }));
    }
    
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validateFields = () => {
    const newErrors = {};
    if (!orderData.status) {
      newErrors.status = t('status_required');
    }
    if (orderData.status === ORDER_STATUS.FINALIZED && !orderData.deliveryDate) {
      newErrors.deliveryDate = t('delivery_date_required');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formatCurrency = (amount) => {
    return formatMoney(amount);
  };

  const handleSave = () => {
    if (validateFields()) {
      const dataToSave = {
        status: orderData.status,
        deliveryDate:
          orderData.status === ORDER_STATUS.FINALIZED
            ? orderData.deliveryDate?.toISOString()
            : null,
      };
      
      console.log('EditOrderStatusModal - Sending status update data:', dataToSave);
      onSave(dataToSave);
    } else {
      SnackbarUtils.error(t('please_correct_errors'));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {getIcon('settings')}
        {t('edit_order_status')}
      </DialogTitle>
      <DialogContent>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>{t('order_information')}</Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">{t('client')}:</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{order?.clientName}</Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">{t('total_amount')}:</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{formatCurrency(order?.totalAmount || 0)}</Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">{t('current_status')}:</Typography>
                <Chip
                  label={getOrderStatusText(order?.status, t)}
                  color={getOrderStatusColor(order?.status)}
                  size="small"
                />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>{t('order_status')}</Typography>
              <Divider sx={{ mb: 2 }} />
              <FormControl fullWidth error={!!errors.status}>
                <InputLabel>{t('order_status')}</InputLabel>
                <Select
                  value={orderData.status}
                  label={t('order_status')}
                  onChange={e => handleFieldChange('status', e.target.value)}
                >
                  {getOrderStatusOptions().map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.status && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {errors.status}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {orderData.status === ORDER_STATUS.FINALIZED && (
              <Grid item xs={12}>
                <DatePicker
                  label={t('delivery_date_optional')}
                  value={orderData.deliveryDate}
                  onChange={date => handleFieldChange('deliveryDate', date)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      error={!!errors.deliveryDate}
                      helperText={errors.deliveryDate}
                    />
                  )}
                />
              </Grid>
            )}
          </Grid>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('cancel')}</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={Object.keys(errors).length > 0 && Object.values(errors).some(e => e !== '')}
        >
          {t('update_status')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditOrderStatusModal;
